import { z } from 'zod';
import { safeParseWhere } from './where/schema.js';
import type { WhereGroup } from './where/types.js';

export const WORKSPACE_VIEW_DATASETS = ['account', 'contact', 'project'] as const;
export type WorkspaceViewDataset = (typeof WORKSPACE_VIEW_DATASETS)[number];

export {
  SYSTEM_VIEWS,
  SYSTEM_VIEW_KINDS,
  type SystemViewKind,
  WATCH_DATASETS,
  type WatchDataset,
} from '@parcel/types';

/** A dynamic View: the Search chip and the condition, both validated for the View's dataset. */
export function dynamicViewDefinitionSchema(dataset: WorkspaceViewDataset) {
  return DYNAMIC_SCHEMAS[dataset];
}

function buildDynamicViewDefinitionSchema(dataset: WorkspaceViewDataset) {
  return z
    .object({
      version: z.literal(2),
      q: z
        .string()
        .max(200)
        .transform((value) => value.trim()),
      where: z.unknown(),
    })
    .strict()
    .transform((definition, ctx): DynamicViewDefinition => {
      const parsed = safeParseWhere(dataset, definition.where);
      if (parsed.ok) return { version: 2, q: definition.q, where: parsed.where };
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['where'], message: parsed.error.message });
      return z.NEVER;
    });
}

export type DynamicViewDefinition = { version: 2; q: string; where: WhereGroup };
export type StaticViewDefinition = { version: 1 };
export type WorkspaceViewPresentation = {
  version: 1;
  sorting: Array<{ id: string; desc: boolean }>;
  column_visibility: Record<string, boolean>;
};
export const EMPTY_WORKSPACE_VIEW_PRESENTATION: WorkspaceViewPresentation = {
  version: 1,
  sorting: [],
  column_visibility: {},
};

// Built once per dataset: stored Views are parsed in loops (nested Views, score dependencies).
const DYNAMIC_SCHEMAS = {
  account: buildDynamicViewDefinitionSchema('account'),
  contact: buildDynamicViewDefinitionSchema('contact'),
  project: buildDynamicViewDefinitionSchema('project'),
};

export function parseDynamicViewDefinition(
  dataset: WorkspaceViewDataset,
  raw: unknown,
): DynamicViewDefinition {
  return dynamicViewDefinitionSchema(dataset).parse(raw);
}

/** A stored View's search and condition when it is a dynamic View the app can read, else null. */
export function readDynamicView(view: {
  dataset: WorkspaceViewDataset;
  view_type?: string;
  definition: unknown;
}): DynamicViewDefinition | null {
  if (view.view_type !== undefined && view.view_type !== 'dynamic') return null;
  const parsed = dynamicViewDefinitionSchema(view.dataset).safeParse(view.definition);
  return parsed.success ? parsed.data : null;
}

export const staticViewDefinitionSchema = z.object({ version: z.literal(1) }).strict();
export const workspaceViewPresentationSchema = z
  .object({
    version: z.literal(1),
    sorting: z.array(z.object({ id: z.string().min(1), desc: z.boolean() }).strict()),
    column_visibility: z.record(z.boolean()),
  })
  .strict();

export const parseStaticViewDefinition = (raw: unknown) => staticViewDefinitionSchema.parse(raw);
export const parseWorkspaceViewPresentation = (raw: unknown) =>
  workspaceViewPresentationSchema.parse(raw);
